import { useState, useCallback, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Send, Users, Search, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "all", label: "All Users" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
];

function getMemberCount(list: any): number {
  return (list as any).memberCount ?? list?.members?.length ?? 0;
}

function UserPicker({ selected, onChange, enabled = true }: { selected: any[]; onChange: (u: any[]) => void; enabled?: boolean }) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const { data, isLoading } = useQuery({
    queryKey: ["/api/admin/users", search, status, page],
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String((page - 1) * PAGE_SIZE) });
      if (search) params.set("search", search);
      if (status !== "all") params.set("subscriptionStatus", status);
      return apiRequest("GET", `/api/admin/users?${params}`) as unknown as Promise<any>;
    },
    placeholderData: (prev: any) => prev,
    enabled,
  });

  const users: any[] = data?.users || data?.data || [];
  const total: number = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const selectedIds = new Set(selected.map((u: any) => String(u.userId)));

  const toggle = (u: any) => {
    if (!u.id) return;
    const uid = String(u.id);
    if (selectedIds.has(uid)) onChange(selected.filter((s: any) => String(s.userId) !== uid));
    else onChange([...selected, { userId: uid, email: u.email, firstName: u.firstName || "" }]);
  };

  const pageIds = new Set(users.map(u => String(u.id)));
  const selectAll = () => {
    const newOnes = users
      .filter(u => !selectedIds.has(String(u.id)))
      .map(u => ({ userId: String(u.id), email: u.email, firstName: u.firstName || "" }));
    onChange([...selected, ...newOnes]);
  };

  const deselectAll = () => onChange(selected.filter((s: any) => !pageIds.has(String(s.userId))));

  const allOnPageSelected = users.length > 0 && users.every(u => selectedIds.has(String(u.id)));

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search name or email..." className="pl-8 h-8 text-sm" />
        </div>
        <Select value={status} onValueChange={v => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b">
          <button type="button" onClick={() => allOnPageSelected ? deselectAll() : selectAll()}
            className="text-xs text-primary underline hover:no-underline">
            {allOnPageSelected ? "Deselect all on page" : "Select all on page"}
          </button>
          <span className="text-xs text-muted-foreground">{selected.length} selected · {total} total</span>
        </div>
        <div className="divide-y max-h-48 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">No users found</div>
          ) : users.map(u => (
            <div key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30">
              <Checkbox checked={selectedIds.has(String(u.id))} onCheckedChange={() => toggle(u)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{u.firstName} {u.lastName}</p>
                <p className="text-xs text-muted-foreground truncate">{u.email}</p>
              </div>
              <Badge variant="outline" className="text-xs shrink-0">{u.subscriptionStatus || "—"}</Badge>
            </div>
          ))}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t bg-muted/30">
            <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft size={14} /></Button>
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
            <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}><ChevronRight size={14} /></Button>
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 items-center">
          {selected.slice(0, 2).map((m: any) => (
            <span key={m.userId} className="flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">
              {m.email}
              <button type="button" onClick={() => onChange(selected.filter((s: any) => s.userId !== m.userId))}><X size={10} /></button>
            </span>
          ))}
          {selected.length > 2 && (
            <span className="text-xs text-muted-foreground">+{selected.length - 2} more</span>
          )}
        </div>
      )}
    </div>
  );
}

function MemberTable({ members, memberFilter, isAllUsers, memberPage, onPageChange, onRemove }: {
  members: any[];
  memberFilter: string;
  isAllUsers: boolean;
  memberPage: number;
  onPageChange: (p: number) => void;
  onRemove: (userId: string) => void;
}) {
  const PAGE_SIZE = 20;
  const filtered = members.filter((m: any) =>
    !memberFilter ||
    m.email?.toLowerCase().includes(memberFilter.toLowerCase()) ||
    m.firstName?.toLowerCase().includes(memberFilter.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((memberPage - 1) * PAGE_SIZE, memberPage * PAGE_SIZE);

  if (!members.length) return <p className="text-center text-muted-foreground py-8">No members yet</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b text-left text-muted-foreground text-xs">
          <th className="pb-2 pr-4">Name</th><th className="pb-2 pr-4">Email</th><th className="pb-2 pr-4">Joined</th><th className="pb-2"></th>
        </tr></thead>
        <tbody>
          {paginated.map((m: any) => (
            <tr key={m.userId} className="border-b hover:bg-muted/50">
              <td className="py-2 pr-4 text-xs">{m.firstName || "—"}</td>
              <td className="py-2 pr-4 text-xs">{m.email}</td>
              <td className="py-2 pr-4 text-xs text-gray-400">{m.addedAt ? new Date(m.addedAt).toLocaleDateString() : "—"}</td>
              <td className="py-2">
                {!isAllUsers && (
                  <button onClick={() => onRemove(m.userId)} className="text-red-500 hover:text-red-700 text-xs">Remove</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <span>{filtered.length} member{filtered.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-1">
            <button disabled={memberPage <= 1} onClick={() => onPageChange(memberPage - 1)} className="px-2 py-1 border rounded disabled:opacity-40">←</button>
            <span className="px-2 py-1">{memberPage} / {totalPages}</span>
            <button disabled={memberPage >= totalPages} onClick={() => onPageChange(memberPage + 1)} className="px-2 py-1 border rounded disabled:opacity-40">→</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DistributionTab() {
  const queryClient = useQueryClient();
  const [openList, setOpenList] = useState<any>(null);
  const [memberPage, setMemberPage] = useState(1);
  const [memberFilter, setMemberFilter] = useState("");

  const [listDialog, setListDialog] = useState(false);
  const [editingList, setEditingList] = useState<any>(null);
  const [blastModal, setBlastModal] = useState<any>(null);
  const [blastTemplateId, setBlastTemplateId] = useState("");
  const [listForm, setListForm] = useState({ name: "", description: "" });
  const [pendingMembers, setPendingMembers] = useState<any[]>([]);

  // Separate state for the "add member" search in manage view
  const [addSearch, setAddSearch] = useState("");
  const [addResults, setAddResults] = useState<any[]>([]);
  const [addSearching, setAddSearching] = useState(false);
  const addDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: lists = [], isLoading } = useQuery({
    queryKey: ["/api/email-mgmt/distribution"],
    queryFn: () => apiRequest("GET", "/api/email-mgmt/distribution"),
  });
  const { data: templates = [] } = useQuery({
    queryKey: ["/api/email-mgmt/templates"],
    queryFn: () => apiRequest("GET", "/api/email-mgmt/templates"),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/email-mgmt/distribution"] });

  const refreshOpenList = useCallback(async (listId?: string) => {
    const id = listId ?? openList?._id;
    if (!id) return;
    const res = await apiRequest("GET", `/api/email-mgmt/distribution/${id}`);
    setOpenList((current: any) => (current?._id === id ? res : current));
  }, [openList?._id]);

  useEffect(() => {
    return () => { if (addDebounceRef.current) clearTimeout(addDebounceRef.current); };
  }, []);

  const handleAddSearch = (q: string) => {
    setAddSearch(q);
    if (addDebounceRef.current) clearTimeout(addDebounceRef.current);
    if (!q.trim()) { setAddResults([]); return; }
    addDebounceRef.current = setTimeout(async () => {
      setAddSearching(true);
      try {
        const res: any = await apiRequest("GET", `/api/admin/users?search=${encodeURIComponent(q)}&limit=20`);
        setAddResults(res.users || res.data || []);
      } catch { setAddResults([]); showError("Search failed"); }
      finally { setAddSearching(false); }
    }, 300);
  };

  const openCreate = () => {
    setEditingList(null);
    setListForm({ name: "", description: "" });
    setPendingMembers([]);
    setListDialog(true);
  };

  const openEdit = (l: any) => {
    setEditingList(l);
    setListForm({ name: l.name, description: l.description || "" });
    setPendingMembers([]);
    setListDialog(true);
  };

  const handleListSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let listId: string;
      if (editingList) {
        await apiRequest("PUT", `/api/email-mgmt/distribution/${editingList._id}`, listForm);
        listId = editingList._id;
      } else {
        const res: any = await apiRequest("POST", "/api/email-mgmt/distribution", listForm);
        listId = res._id;
      }
      if (pendingMembers.length) {
        await apiRequest("POST", `/api/email-mgmt/distribution/${listId}/members`, { members: pendingMembers });
      }
      showSuccess(editingList ? "List updated" : "List created");
      setListDialog(false);
      setPendingMembers([]);
      invalidate();
    } catch { showError("Failed to save"); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this list?")) return;
    try {
      await apiRequest("DELETE", `/api/email-mgmt/distribution/${id}`);
      showSuccess("Deleted");
      if (openList?._id === id) setOpenList(null);
      invalidate();
    } catch { showError("Failed"); }
  };

  const handleRemoveMember = async (userId: string) => {
    const listId = openList?._id;
    if (!listId) return;
    try {
      await apiRequest("DELETE", `/api/email-mgmt/distribution/${listId}/members/${userId}`);
      showSuccess("Member removed");
      await refreshOpenList(listId);
    } catch { showError("Failed to remove member"); }
  };

  const handleAddAllUsers = async () => {
    const listId = openList?._id;
    if (!listId || !confirm("Add ALL registered users to this list?")) return;
    try {
      const res: any = await apiRequest("POST", `/api/email-mgmt/distribution/${listId}/members`, { allUsers: true });
      showSuccess(`Added ${res.added} new members`);
      await refreshOpenList(listId);
    } catch { showError("Failed"); }
  };

  const handleAddOneToOpen = async (u: any) => {
    const listId = openList?._id;
    if (!listId) return;
    try {
      await apiRequest("POST", `/api/email-mgmt/distribution/${listId}/members`, {
        members: [{ userId: String(u.id), email: u.email, firstName: u.firstName || "" }],
      });
      showSuccess(`Added ${u.email}`);
      await refreshOpenList(listId);
      setAddSearch(""); setAddResults([]);
    } catch { showError("Failed"); }
  };

  const handleBlast = async () => {
    if (!blastModal?._id) return;
    if (!blastTemplateId) return showError("Select a template");
    try {
      const res: any = await apiRequest("POST", `/api/email-mgmt/distribution/${blastModal._id}/blast`, { templateId: blastTemplateId });
      showSuccess(`Sent ${res.sent} emails${res.failed ? `, ${res.failed} failed` : ""}`);
      setBlastModal(null); setBlastTemplateId("");
    } catch { showError("Failed to send blast"); }
  };

  // ── Manage view ──────────────────────────────────────────────────────────
  if (openList) return (
    <div>
      <button onClick={() => { setOpenList(null); setAddSearch(""); setAddResults([]); setBlastModal(null); setBlastTemplateId(""); setMemberFilter(""); setMemberPage(1); }} className="text-sm text-primary hover:underline mb-4 flex items-center gap-1">← Back to Lists</button>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h2 className="text-lg font-bold">{openList.name}</h2>
          {openList.description && <p className="text-sm text-muted-foreground">{openList.description}</p>}
          <p className="text-xs text-gray-400 mt-1">
            {getMemberCount(openList)} member{getMemberCount(openList) !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => { setBlastModal(openList); setBlastTemplateId(""); }} className="flex items-center gap-2 bg-green-600 hover:bg-green-700">
            <Send size={14} /> Send Blast
          </Button>
          <Button size="sm" variant="outline" onClick={handleAddAllUsers} className="flex items-center gap-2">
            <Users size={14} /> Add All Users
          </Button>
        </div>
      </div>

      <div className="mb-4 border rounded-lg p-3 bg-muted/50">
        <Label className="text-xs mb-2 block">Add member by name / email</Label>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          <Input value={addSearch} onChange={e => handleAddSearch(e.target.value)}
            placeholder="Search users..." className="pl-8" />
        </div>
        {addSearching && <p className="text-xs text-gray-400 mt-1">Searching...</p>}
        {addResults.length > 0 && (
          <div className="border rounded-lg divide-y mt-2 max-h-48 overflow-y-auto bg-background">
            {addResults.map(u => (
              <div key={u.id} className="flex justify-between items-center px-3 py-2 hover:bg-muted/50">
                <div><p className="text-sm font-medium">{u.firstName} {u.lastName}</p><p className="text-xs text-muted-foreground">{u.email}</p></div>
                <Button size="sm" className="text-xs h-7" onClick={() => handleAddOneToOpen(u)}>Add</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mb-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
          <Input
            value={memberFilter}
            onChange={e => { setMemberFilter(e.target.value); setMemberPage(1); }}
            placeholder="Filter members..."
            className="pl-8 h-8 text-sm"
          />
        </div>
      </div>

      <MemberTable
        members={openList.members || []}
        memberFilter={memberFilter}
        isAllUsers={!!openList.isAllUsers}
        memberPage={memberPage}
        onPageChange={setMemberPage}
        onRemove={handleRemoveMember}
      />

      <Dialog open={!!blastModal} onOpenChange={() => setBlastModal(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Send Blast Email</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Sending to <strong>{blastModal ? getMemberCount(blastModal) : 0}</strong> members of <strong>{blastModal?.name}</strong>
          </p>
          <select value={blastTemplateId} onChange={e => setBlastTemplateId(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:outline-hidden">
            <option value="">— Select template —</option>
            {(templates as any[]).map((t: any) => <option key={t._id} value={t._id}>{t.name}</option>)}
          </select>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setBlastModal(null)}>Cancel</Button>
            <Button onClick={handleBlast} className="flex items-center gap-2 bg-green-600 hover:bg-green-700"><Send size={16} /> Send</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  // ── Lists overview ────────────────────────────────────────────────────────
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-muted-foreground text-sm">{(lists as any[]).length} list{(lists as any[]).length !== 1 ? "s" : ""}</p>
        <Button onClick={openCreate} size="sm" className="flex items-center gap-2"><Plus size={16} /> New List</Button>
      </div>

      {isLoading ? <p className="text-center text-gray-400 py-8">Loading...</p>
        : (lists as any[]).length === 0 ? <p className="text-center text-muted-foreground py-8">No distribution lists yet</p>
        : (
          <div className="space-y-3">
            {(lists as any[]).map((l: any) => (
              <div key={l._id} className="border rounded-lg p-4 hover:shadow-xs flex justify-between items-start">
                <div>
                  <p className="font-semibold">{l.name}</p>
                  {l.description && <p className="text-xs text-muted-foreground mt-0.5">{l.description}</p>}
                  <p className="text-xs text-gray-400 mt-1">
                    {l.isAllUsers ? "All active users (dynamic)" : `${getMemberCount(l)} member${getMemberCount(l) !== 1 ? "s" : ""}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="text-xs h-7" onClick={() => { setOpenList(l); setMemberPage(1); setMemberFilter(""); }}>Manage</Button>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => openEdit(l)}>Edit</Button>
                  <Button size="sm" variant="destructive" className="text-xs h-7" onClick={() => handleDelete(l._id)}>Delete</Button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={listDialog} onOpenChange={setListDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingList ? "Edit" : "Create"} Distribution List</DialogTitle></DialogHeader>
          <form onSubmit={handleListSubmit} className="space-y-4">
            <div>
              <Label>List Name *</Label>
              <Input required value={listForm.name} onChange={e => setListForm({ ...listForm, name: e.target.value })} className="mt-1" placeholder="e.g. Newsletter Subscribers" />
            </div>
            <div>
              <Label>Description</Label>
              <textarea value={listForm.description} onChange={e => setListForm({ ...listForm, description: e.target.value })}
                rows={2} placeholder="What is this list for?"
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-hidden resize-none" />
            </div>
            <div className="border rounded-lg p-4 bg-muted/50 space-y-3">
              <Label>Members</Label>
              <UserPicker selected={pendingMembers} onChange={setPendingMembers} enabled={listDialog} />
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setListDialog(false)}>Cancel</Button>
              <Button type="submit">{editingList ? "Update" : "Create"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
